import {
  IndicatorFactType,
  PlotReportFactElement,
  PlotReportFactLabelFilter,
  R1FactType,
  REPORTING_FACT_BENCHMARKS,
} from '@/api/rest/resources/types/fact';
import {
  GET_ORGANIZATION_GRAPH_FACTS_PATH,
  GET_ORGANIZATION_REPORTING_FACTS_PATH,
  OrganizationReportingFacts,
} from '@/api/rest/resources/types/organization';
import { useOrganizationHiddenFacts } from '@/hooks/useOrganizationHiddenFacts';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { assertIsDefined } from '@/utils/assert';
import { isNullish } from '@/utils/booleans';

import { useOrganizationFacts } from './useOrganizationFacts';
import { filterValidLabels, getFact } from './utils';

const useOrganizationFact = <T = unknown>(
  reportingFacts: OrganizationReportingFacts,
  type: IndicatorFactType | IndicatorFactType[],
  labelFilters?: PlotReportFactLabelFilter,
) => {
  const hiddenFacts = useOrganizationHiddenFacts();

  const factTypes = Array.isArray(type) ? type : [type];
  const labels = labelFilters ? filterValidLabels(labelFilters) : {};

  return factTypes.reduce(
    (acc, factType) => {
      const isFactHidden = hiddenFacts.includes(factType);

      const fact = getFact<T>(reportingFacts, factType, labels, isFactHidden);
      if (fact) {
        return {
          ...acc,
          [factType]: fact,
        };
      }
      return acc;
    },
    {} as Record<IndicatorFactType, PlotReportFactElement<T | null> | undefined>,
  );
};

const useGetOrganizationFactParams = (organizationIdArg?: string | null, labelFilters?: PlotReportFactLabelFilter) => {
  const orgId = useOrganizationId();
  const organizationId = organizationIdArg ?? orgId;
  assertIsDefined(organizationId, 'organizationId');

  // Since BE ignore label filters for benchmark and landuse, we filter those out before hitting the API to avoid multiple duplicate calls
  const reportFactLabelFilters = labelFilters ? filterValidLabels(labelFilters) : {};

  return {
    reportFactLabelFilters,
    organizationId,
  };
};

export const useOrganizationIndicatorReportingFact = <T = unknown>(
  type: IndicatorFactType | IndicatorFactType[],
  organizationIdArg?: string | null,
  labelFilters?: PlotReportFactLabelFilter,
  plotGroupId?: string | null,
) => {
  const { reportFactLabelFilters, organizationId } = useGetOrganizationFactParams(organizationIdArg, labelFilters);

  const queryParams = {
    ...reportFactLabelFilters,
    fact_names: Array.isArray(type)
      ? type.filter((fact) => !isNullish(fact) && fact !== R1FactType.unknown).join(',')
      : type,
  };

  const organizationReportingFacts = useOrganizationFacts(
    organizationId,
    GET_ORGANIZATION_REPORTING_FACTS_PATH,
    queryParams,
    plotGroupId,
  ).data;

  return useOrganizationFact<T>(organizationReportingFacts, type, labelFilters);
};

/**
 * @param organizationId Falls back to the organizationId from the user if no value is provided.
 */
export const useOrganizationIndicatorGraphFact = <T = unknown>(
  type: IndicatorFactType | IndicatorFactType[],
  organizationIdArg?: string | null,
  labelFilters?: PlotReportFactLabelFilter,
  plotGroupId?: string | null,
) => {
  const { reportFactLabelFilters, organizationId } = useGetOrganizationFactParams(organizationIdArg, labelFilters);

  const queryParams = {
    ...reportFactLabelFilters,
    fact_names: Array.isArray(type)
      ? type.filter((fact) => !isNullish(fact) && fact !== R1FactType.unknown).join(',')
      : type,
  };

  let queryUrl: typeof GET_ORGANIZATION_GRAPH_FACTS_PATH | typeof GET_ORGANIZATION_REPORTING_FACTS_PATH =
    GET_ORGANIZATION_GRAPH_FACTS_PATH;

  if (!Array.isArray(type) && REPORTING_FACT_BENCHMARKS.includes(type as R1FactType)) {
    queryUrl = GET_ORGANIZATION_REPORTING_FACTS_PATH;
  }

  const organizationReportingFacts = useOrganizationFacts(organizationId, queryUrl, queryParams, plotGroupId).data;

  return useOrganizationFact<T>(organizationReportingFacts, type, labelFilters);
};
